import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { fetchShopSyncStatus } from "@/lib/amazon-api";
import {
  SSE_FALLBACK_POLL_MS,
  SSE_SAFETY_POLL_MS,
  subscribeToSseInvalidation,
} from "@/lib/sse-invalidation";
import { useAuthStore } from "@/stores/auth";

export const shopSyncStatusKey = (shopId: string | null) => ["sync", "status", shopId] as const;

/** Canonical shop sync snapshot with SSE invalidation and slow failure fallback. */
export function useShopSyncStatus() {
  const queryClient = useQueryClient();
  const shopId = useAuthStore((state) => state.currentShopId);
  const [sseFailed, setSseFailed] = useState(false);
  const query = useQuery({
    queryKey: shopSyncStatusKey(shopId),
    queryFn: () => fetchShopSyncStatus(shopId!),
    enabled: Boolean(shopId),
    refetchInterval: sseFailed ? SSE_FALLBACK_POLL_MS : SSE_SAFETY_POLL_MS,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (!shopId) return;
    return subscribeToSseInvalidation(queryClient, {
      id: `shop-sync:${shopId}`,
      url: `/api/v1/sync/shops/${encodeURIComponent(shopId)}/events`,
      eventNames: ["run.changed"],
      queryKey: shopSyncStatusKey(shopId),
      onConnectionChange: (state) => setSseFailed(state === "error"),
    });
  }, [queryClient, shopId]);

  return query;
}
